import { getConstraintDisplayName } from "../../utils/constraintNames";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { SCENE_GRAPH_NODE_RADIUS as NODE_RADIUS } from "../../utils/sceneGraphNodeAppearance";
import { FIXED_JOINT_GROUND_STEM_PX, GLOBAL_SPACING } from "./constants";
import { truncateLabel } from "./helpers";

function placeInColumn({ nodes, target, uniformSatXOffset, satelliteYGap }) {
  const satX =
    target.satX != null && target.type !== "body" ? target.satX : target.x + uniformSatXOffset;
  const colNodes =
    target.type === "body"
      ? nodes.filter((node) => node.parentBodyId === target.id || node._satHostId === target.id)
      : nodes.filter((node) => Math.abs(node.x - satX) < 0.5);
  const bottomY = colNodes.length > 0 ? Math.max(...colNodes.map((node) => node.y)) : target.y;
  return { satX, eqY: bottomY + satelliteYGap };
}

export function addJoints({ nodes, edges, idState, bodyTree, bodies, joints, context }) {
  const { hiddenTypes, bodiesHidden, bodyMap, bodyHasFreeJoint, uniformSatXOffset, satelliteYGap } =
    context;
  if (hiddenTypes.has("joint")) return;

  const midEdgeJointUuids = new Set();
  const fixedRootSupportByBodyUuid = new Map();
  bodyTree.forEach((rootBody) => {
    if (!rootBody?.uuid) return;
    const bodyNode = nodes.find((node) => node.type === "body" && node.uuid === rootBody.uuid);
    if (!bodyNode) return;
    if (bodyHasFreeJoint(rootBody)) return;

    edges.push({
      id: `e-fixed-ground-${bodyNode.id}`,
      from: bodyNode.id,
      edgeType: "fixed-ground",
      rootBodyUuid: rootBody.uuid,
      groundX: bodyNode.x,
      groundY: bodyNode.y - FIXED_JOINT_GROUND_STEM_PX,
    });
    fixedRootSupportByBodyUuid.set(rootBody.uuid, {
      x: bodyNode.x,
      groundY: bodyNode.y - FIXED_JOINT_GROUND_STEM_PX,
      bodyTopY: bodyNode.y - (NODE_RADIUS.body ?? 16),
    });
  });

  if (!bodiesHidden) {
    edges
      .filter((edge) => edge.edgeType === "body-parent-child")
      .forEach((edge) => {
        const parentNode = nodes.find((node) => node.id === edge.from);
        const childNode = nodes.find((node) => node.id === edge.to);
        if (!parentNode || !childNode) return;
        const childBody = bodyMap.get(childNode.uuid);
        if (!childBody?.joints?.length) return;
        childBody.joints.forEach((jointEntry, jointIndex) => {
          const jointUuid = typeof jointEntry === "string" ? jointEntry : jointEntry?.uuid;
          if (!jointUuid) return;
          const jointDef = joints?.[jointUuid];
          if (jointDef?.kind === "free") return;
          midEdgeJointUuids.add(jointUuid);
          const diagStartY = edge.crossbarY != null ? edge.crossbarY : parentNode.y;
          nodes.push({
            id: idState.nextId++,
            name: jointDef?.name || `joint_${jointIndex}`,
            displayName: truncateLabel(jointDef?.name || `joint_${jointIndex}`),
            uuid: jointUuid,
            type: "joint",
            x: (parentNode.x + childNode.x) / 2,
            y: (diagStartY + childNode.y) / 2,
            satX: childNode.x + uniformSatXOffset,
            _childBodyId: childNode.id,
          });
        });
      });
  }

  Object.values(bodies || {}).forEach((body) => {
    if (!body?.uuid || !Array.isArray(body.joints)) return;
    const bodyNode = nodes.find((node) => node.type === "body" && node.uuid === body.uuid);
    if (!bodyNode) return;

    body.joints.forEach((jointEntry, jointIndex) => {
      const jointUuid = typeof jointEntry === "string" ? jointEntry : jointEntry?.uuid;
      if (!jointUuid || midEdgeJointUuids.has(jointUuid)) return;
      const jointDef = joints?.[jointUuid];
      if (jointDef?.kind === "free") return;

      const rootSupport = fixedRootSupportByBodyUuid.get(body.uuid);
      if (
        rootSupport &&
        (jointDef?.kind === "hinge" || jointDef?.kind === "slide" || jointDef?.kind === "ball")
      ) {
        const rootLineJoints = body.joints
          .map((entry) => (typeof entry === "string" ? entry : entry?.uuid))
          .filter((id) => {
            const kind = joints?.[id]?.kind;
            return kind === "hinge" || kind === "slide" || kind === "ball";
          });
        const rootLineIndex = Math.max(0, rootLineJoints.indexOf(jointUuid));
        const rootLineCount = Math.max(1, rootLineJoints.length);
        const t = (rootLineIndex + 1) / (rootLineCount + 1);
        nodes.push({
          id: idState.nextId++,
          name: jointDef?.name || `joint_${jointIndex}`,
          displayName: truncateLabel(jointDef?.name || `joint_${jointIndex}`),
          uuid: jointUuid,
          type: "joint",
          x: rootSupport.x,
          y: rootSupport.bodyTopY + (rootSupport.groundY - rootSupport.bodyTopY) * t,
          satX: rootSupport.x + uniformSatXOffset,
          parentBodyId: bodyNode.id,
          _childBodyId: bodyNode.id,
        });
        return;
      }

      const { satX, eqY } = placeInColumn({
        nodes,
        target: bodyNode,
        uniformSatXOffset,
        satelliteYGap,
      });
      const jointNode = {
        id: idState.nextId++,
        name: jointDef?.name || `joint_${jointIndex}`,
        displayName: truncateLabel(jointDef?.name || `joint_${jointIndex}`),
        uuid: jointUuid,
        type: "joint",
        x: satX,
        y: eqY,
        satX,
        parentBodyId: bodyNode.id,
        _childBodyId: bodyNode.id,
      };
      nodes.push(jointNode);
      edges.push({
        id: `e-body-sat-joint-${bodyNode.id}-${jointNode.id}`,
        from: bodyNode.id,
        to: jointNode.id,
        edgeType: "body-satellite",
      });
    });
  });
}

export function buildTendonByName(tendons) {
  const tendonByName = new Map();
  Object.values(tendons ?? {}).forEach((tendon) => {
    if (tendon?.name) tendonByName.set(tendon.name, tendon);
  });
  return tendonByName;
}

export function addTendons({
  nodes,
  edges,
  idState,
  bodies,
  tendons,
  actuators,
  nodesByUuid,
  tendonByName,
  hiddenTypes,
  globalY,
}) {
  const tendonUuidHasActuator = new Set();
  Object.values(actuators ?? {}).forEach((actuator) => {
    const tendonUuid = actuator.tendonUuid || tendonByName.get(actuator.tendon || "")?.uuid || null;
    if (tendonUuid && tendons?.[tendonUuid]) tendonUuidHasActuator.add(tendonUuid);
  });

  if (hiddenTypes.has("tendon")) return;

  let tendonFallbackIndex = 0;
  Object.values(tendons ?? {}).forEach((tendon) => {
    if (!tendon?.uuid) return;

    if (tendon.kind === "spatial" && Array.isArray(tendon.spatial) && tendon.spatial.length >= 2) {
      const memberNodes = tendon.spatial
        .filter((ref) => ref?.kind === "site" && ref?.site)
        .map((ref) => {
          const siteUuid = ref.site;
          const ownerBody = Object.values(bodies || {}).find(
            (body) =>
              Array.isArray(body?.sites) &&
              body.sites.some((siteRef) => {
                const id = typeof siteRef === "string" ? siteRef : siteRef?.uuid;
                return id === siteUuid;
              })
          );
          if (ownerBody)
            return (
              nodes.find((node) => node.type === "body" && node.uuid === ownerBody.uuid) ?? null
            );
          return (
            nodes.find(
              (node) =>
                (node.type === "site" || node.type === "attachment_site") && node.uuid === siteUuid
            ) ?? null
          );
        })
        .filter(Boolean)
        .filter((node, index, allNodes) => index === 0 || node.id !== allNodes[index - 1].id);

      if (memberNodes.length >= 2) {
        nodesByUuid.set(tendon.uuid, memberNodes[0]);
        for (let ci = 0; ci < memberNodes.length - 1; ci++) {
          const a = memberNodes[ci];
          const b = memberNodes[ci + 1];
          edges.push({
            id: `e-tendon-sites-${tendon.uuid}-${a.id}-${b.id}`,
            from: a.id,
            to: b.id,
            edgeType: "tendon-joint",
            tendonUuid: tendon.uuid,
            tendonHasActuator: tendonUuidHasActuator.has(tendon.uuid),
          });
        }
        return;
      }
    }

    const memberJointNodes = Array.isArray(tendon.joints)
      ? tendon.joints
          .map((jointRef) => {
            const uuid = typeof jointRef === "string" ? jointRef : jointRef?.joint;
            return uuid ? nodesByUuid.get(uuid) : null;
          })
          .filter(Boolean)
      : [];

    if (memberJointNodes.length === 0) {
      const tendonNode = {
        id: idState.nextId++,
        name: tendon.name || "tendon",
        displayName: truncateLabel(tendon.name || "tendon"),
        uuid: tendon.uuid,
        tendonUuid: tendon.uuid,
        type: "tendon",
        x: -60 + tendonFallbackIndex * GLOBAL_SPACING,
        y: globalY,
        data: tendon,
      };
      nodes.push(tendonNode);
      nodesByUuid.set(tendon.uuid, tendonNode);
      tendonFallbackIndex++;
      return;
    }

    const firstJoint = memberJointNodes[0];
    nodesByUuid.set(tendon.uuid, firstJoint);
    for (let ci = 0; ci < memberJointNodes.length - 1; ci++) {
      const a = memberJointNodes[ci];
      const b = memberJointNodes[ci + 1];
      edges.push({
        id: `e-tendon-joints-${tendon.uuid}-${a.id}-${b.id}`,
        from: a.id,
        to: b.id,
        edgeType: "tendon-joint",
        tendonUuid: tendon.uuid,
        tendonHasActuator: tendonUuidHasActuator.has(tendon.uuid),
      });
    }
  });
}

export function addActuators({
  nodes,
  edges,
  idState,
  actuators,
  joints,
  nodesByUuid,
  tendonByName,
  context,
  globalY,
}) {
  const { hiddenTypes, actuatorXOffset, actuatorYStart, actuatorYGap } = context;
  const actuatorsByJoint = new Map();
  const actuatorsByTendon = new Map();
  const unparentedActuators = [];

  if (!hiddenTypes.has("actuator")) {
    Object.values(actuators ?? {}).forEach((actuator) => {
      const jointRef = typeof actuator.joint === "string" ? actuator.joint : null;
      const resolvedJointUuid = jointRef ? resolveJointUuidFromRef(jointRef, joints ?? {}) : null;

      if (resolvedJointUuid && nodesByUuid.has(resolvedJointUuid)) {
        const list = actuatorsByJoint.get(resolvedJointUuid) ?? [];
        list.push(actuator);
        actuatorsByJoint.set(resolvedJointUuid, list);
        return;
      }

      const referencedTendonUuid =
        actuator.tendonUuid || tendonByName.get(actuator.tendon || "")?.uuid || null;
      if (referencedTendonUuid) {
        if (nodesByUuid.has(referencedTendonUuid)) {
          const list = actuatorsByTendon.get(referencedTendonUuid) ?? [];
          list.push(actuator);
          actuatorsByTendon.set(referencedTendonUuid, list);
        }
        return;
      }

      unparentedActuators.push(actuator);
    });
  }

  const jointActuatorSlotNext = new Map();
  actuatorsByJoint.forEach((actuatorList, jointUuid) => {
    const jointNode = nodesByUuid.get(jointUuid);
    // Joints hidden -> actuators are forced hidden too (effectiveHiddenTypes).
    if (!jointNode) return;

    actuatorList.forEach((actuator) => {
      const slot = jointActuatorSlotNext.get(jointUuid) ?? 0;
      const actuatorNode = {
        id: idState.nextId++,
        name: actuator.name || "actuator",
        displayName: truncateLabel(actuator.name || "actuator"),
        uuid: actuator.uuid,
        type: "actuator",
        x: jointNode.x + actuatorXOffset,
        y: jointNode.y + actuatorYStart + slot * actuatorYGap,
        data: actuator,
      };
      nodes.push(actuatorNode);
      nodesByUuid.set(actuator.uuid, actuatorNode);
      edges.push({
        id: `e-act-${jointNode.id}-${actuatorNode.id}`,
        from: jointNode.id,
        to: actuatorNode.id,
        edgeType: "actuator-joint",
      });
      jointActuatorSlotNext.set(jointUuid, slot + 1);
    });
  });

  if (!hiddenTypes.has("actuator")) {
    actuatorsByTendon.forEach((actuatorList, tendonUuid) => {
      const anchorNode = nodesByUuid.get(tendonUuid);
      if (!anchorNode) return;

      if (anchorNode.type === "joint") {
        const jointUuid = anchorNode.uuid;
        actuatorList.forEach((actuator) => {
          const slot = jointActuatorSlotNext.get(jointUuid) ?? 0;
          const actuatorNode = {
            id: idState.nextId++,
            name: actuator.name || "actuator",
            displayName: truncateLabel(actuator.name || "actuator"),
            uuid: actuator.uuid,
            type: "actuator",
            x: anchorNode.x + actuatorXOffset,
            y: anchorNode.y + actuatorYStart + slot * actuatorYGap,
            data: actuator,
          };
          nodes.push(actuatorNode);
          nodesByUuid.set(actuator.uuid, actuatorNode);
          edges.push({
            id: `e-act-tendon-${anchorNode.id}-${actuatorNode.id}`,
            from: anchorNode.id,
            to: actuatorNode.id,
            edgeType: "actuator-tendon",
          });
          jointActuatorSlotNext.set(jointUuid, slot + 1);
        });
        return;
      }

      actuatorList.forEach((actuator, index) => {
        const actuatorNode = {
          id: idState.nextId++,
          name: actuator.name || "actuator",
          displayName: truncateLabel(actuator.name || "actuator"),
          uuid: actuator.uuid,
          type: "actuator",
          x: anchorNode.x + actuatorXOffset,
          y: anchorNode.y + actuatorYStart + index * actuatorYGap,
          data: actuator,
        };
        nodes.push(actuatorNode);
        nodesByUuid.set(actuator.uuid, actuatorNode);
        edges.push({
          id: `e-act-tendon-${anchorNode.id}-${actuatorNode.id}`,
          from: anchorNode.id,
          to: actuatorNode.id,
          edgeType: "actuator-tendon",
        });
      });
    });
  }

  if (unparentedActuators.length > 0) {
    const totalGlobalWidth = (unparentedActuators.length - 1) * GLOBAL_SPACING;
    let gx = -totalGlobalWidth / 2;
    unparentedActuators.forEach((actuator) => {
      const globalNode = {
        id: idState.nextId++,
        name: actuator.name || "actuator",
        uuid: actuator.uuid,
        type: "actuator",
        x: gx,
        y: globalY,
        data: actuator,
      };
      nodes.push(globalNode);
      if (actuator.uuid) nodesByUuid.set(actuator.uuid, globalNode);
      gx += GLOBAL_SPACING;
    });
  }
}

export function addEqualities({
  nodes,
  edges,
  idState,
  equalities,
  nodesByUuid,
  hiddenTypes,
  globalY,
}) {
  if (hiddenTypes.has("equality")) return;

  const equalityList = Object.values(equalities ?? {});
  let equalityFallbackIndex = 0;
  equalityList.forEach((equality) => {
    const target1 = equality.obj1 ? nodesByUuid.get(equality.obj1) : null;
    const target2 = equality.obj2 ? nodesByUuid.get(equality.obj2) : null;

    if (target1 && target2 && target1.id !== target2.id) {
      nodesByUuid.set(equality.uuid, target1);
      edges.push({
        id: `e-eq-arc-${equality.uuid}-${target1.id}-${target2.id}`,
        from: target1.id,
        to: target2.id,
        edgeType: "equality-ref",
        equalityUuid: equality.uuid,
      });
      return;
    }

    const name = getConstraintDisplayName(equality, equalities);
    const equalityNode = {
      id: idState.nextId++,
      name,
      displayName: truncateLabel(name),
      uuid: equality.uuid,
      type: "equality",
      x: -60 + equalityFallbackIndex * GLOBAL_SPACING,
      y: globalY,
      data: equality,
    };
    nodes.push(equalityNode);
    nodesByUuid.set(equality.uuid, equalityNode);
    equalityFallbackIndex++;
  });
}
